import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Button,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { PaymentStatus } from 'types/payments'
import { getMassPayoutCryptoBatchStatus, getMassPayoutStatusType, titleCase } from 'utils/utils'
import { BatchStatus, FiatPayout, MassPayoutCryptoBatch } from '../types'

interface UseMassPayoutsBatchesTableDataProps {
  activeTab: string
  // fiat
  fiatData: FiatPayout[]
  onOpenFiatSidePanel: (index: number) => void
  goToFiatMassPayoutBatch: (batch: FiatPayout) => void
  // crypto
  cryptoData: MassPayoutCryptoBatch[]
  isCrypto: boolean
  onOpenCryptoSidePanel: (index: number) => void
  goToCryptoMassPayoutBatch: (batch: MassPayoutCryptoBatch, activeTab: string) => void
}
interface MassPayoutsBatchesTableDataProps {
  columns: DataTableHeaderProps[]
  data: DataTableRowData[]
}

export const useMassPayoutsBatchesTableData = ({
  activeTab,
  // fiat
  fiatData,
  onOpenFiatSidePanel,
  goToFiatMassPayoutBatch,
  // crypto
  isCrypto,
  cryptoData,
  onOpenCryptoSidePanel,
  goToCryptoMassPayoutBatch,
}: UseMassPayoutsBatchesTableDataProps): MassPayoutsBatchesTableDataProps => {
  const { t } = useTranslation()
  const permissions = useMappedRoles()
  const canEdit = permissions?.massPayouts?.canEdit

  const fiatDataTableColumns = (): DataTableHeaderProps[] => {
    return [
      {
        key: 'batch',
        label: t('massPayouts.batch'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'date',
        label: t('massPayouts.date'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'uploadedBy',
        label: t('massPayouts.uploadedBy'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'status',
        label: t('massPayouts.status'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ]
  }

  const cryptoDataTableColumns = (): DataTableHeaderProps[] => {
    return [
      {
        key: 'batch',
        label: t('massPayouts.batch'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'date',
        label: t('massPayouts.date'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'payoutsProcessed',
        label: t('massPayouts.payoutsProcessed'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'uploadedBy',
        label: t('massPayouts.uploadedBy'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'status',
        label: t('massPayouts.status'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ]
  }

  const getCryptoTableData = (cryptoData: MassPayoutCryptoBatch[]): DataTableRowData[] => {
    if (!cryptoData) return []

    return cryptoData?.map((cryptoBatch, index) => {
      return {
        id: cryptoBatch?.externalId,
        testid: cryptoBatch?.externalId,
        onRowClick: () => onOpenCryptoSidePanel(index),
        cells: [
          {
            key: 'batch',
            variant: DataTableCellVariants.TEXT,
            Text: cryptoBatch?.fileName,
          },
          {
            key: 'date',
            variant: DataTableCellVariants.TEXT,
            Text: dayjs(cryptoBatch?.createdAt).format(DateFormat.DD_MMMM_YYYY),
          },
          {
            key: 'payoutsProcessed',
            variant: DataTableCellVariants.TEXT,
            Text: `${cryptoBatch?.totalProcessedItems}/${cryptoBatch?.totalItems}`,
          },
          {
            key: 'uploadedBy',
            variant: DataTableCellVariants.TEXT,
            Text: cryptoBatch?.userName,
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Highlights: [
              <Highlight
                key={`${cryptoBatch?.externalId}-status`}
                type={getMassPayoutStatusType(cryptoBatch?.status?.toUpperCase())}
              >
                {titleCase(getMassPayoutCryptoBatchStatus(cryptoBatch?.status))}
              </Highlight>,
            ],
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: [
              <DropdownMenu
                key={`context-menu-${cryptoBatch?.externalId}`}
                DropdownMenuItems={[
                  <DropdownMenuItem
                    key={`${cryptoBatch?.externalId}-details`}
                    LeadingIcon={<Icon name="Info" />}
                    onClick={() => onOpenCryptoSidePanel(index)}
                  >
                    {t('details')}
                  </DropdownMenuItem>,
                  ...(cryptoBatch?.status?.toUpperCase() === BatchStatus.DRAFT && canEdit
                    ? [
                        <DropdownMenuItem
                          key={`${cryptoBatch?.externalId}-edit`}
                          LeadingIcon={<Icon name="SimplePencilCustom" />}
                          onClick={() => goToCryptoMassPayoutBatch(cryptoBatch, activeTab)}
                        >
                          {t('massPayouts.editDraft')}
                        </DropdownMenuItem>,
                      ]
                    : []),
                ]}
              >
                <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
              </DropdownMenu>,
            ],
          },
        ],
      }
    })
  }

  const getFiatTableData = (fiatData: FiatPayout[]): DataTableRowData[] => {
    if (!fiatData) return []

    return fiatData?.map((fiatBatch, index) => {
      return {
        id: fiatBatch?.reference,
        testid: fiatBatch?.reference,
        cells: [
          {
            key: 'batch',
            variant: DataTableCellVariants.TEXT,
            Text: fiatBatch?.alias,
          },
          {
            key: 'date',
            variant: DataTableCellVariants.TEXT,
            Text: dayjs(fiatBatch?.createdAt).format(DateFormat.DD_MMMM_YYYY),
          },
          {
            key: 'uploadedBy',
            variant: DataTableCellVariants.TEXT,
            Text: fiatBatch?.createdBy?.fullName,
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Highlights: [
              <Highlight
                key={`${fiatBatch?.reference}-status`}
                type={getMassPayoutStatusType(fiatBatch?.status?.toUpperCase())}
              >
                {titleCase(getMassPayoutCryptoBatchStatus(fiatBatch?.status))}
              </Highlight>,
            ],
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: [
              <DropdownMenu
                key={`context-menu-${fiatBatch?.reference}`}
                DropdownMenuItems={[
                  <DropdownMenuItem
                    key={`${fiatBatch?.reference}-details`}
                    LeadingIcon={<Icon name="Info" />}
                    onClick={() => onOpenFiatSidePanel(index)}
                  >
                    {t('details')}
                  </DropdownMenuItem>,
                  ...(fiatBatch?.status !== PaymentStatus.PROCESSING &&
                  fiatBatch?.status !== PaymentStatus.COMPLETED &&
                  canEdit
                    ? [
                        <DropdownMenuItem
                          key={`${fiatBatch?.reference}-edit`}
                          LeadingIcon={<Icon name="SimplePencilCustom" />}
                          onClick={() => goToFiatMassPayoutBatch(fiatBatch)}
                        >
                          {t('massPayouts.editDraft')}
                        </DropdownMenuItem>,
                      ]
                    : []),
                ]}
              >
                <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
              </DropdownMenu>,
            ],
          },
        ],
        ...(canEdit && {
          onRowClick: () => goToFiatMassPayoutBatch(fiatBatch),
        }),
      }
    })
  }

  return useMemo(() => {
    return {
      columns: isCrypto ? cryptoDataTableColumns() : fiatDataTableColumns(),
      data: isCrypto ? getCryptoTableData(cryptoData) : getFiatTableData(fiatData),
    }
  }, [isCrypto, cryptoData, fiatData])
}
